/**
 * ServeKeypad (tablet mode, PROD-1278 feedback → PROD-1428 items 12 + 14) —
 * the on-screen keyboard of the serve screen, in two layouts
 * (`lib/cafeteria/virtual-keyboard.ts` decides which from the orientation):
 *
 *   - `column` (landscape): down the RIGHT column, under a right hand — a
 *     large readout of what has been typed with an ✕ to clear it, the
 *     phone-layout number pad (1-2-3 on top, blank · 0 · ⌫), then the letter
 *     rows (a second ⌫ ends the z–m row, as on the iPad) and the space bar.
 *   - `bottom` (portrait): across the BOTTOM of the screen, full width, like
 *     a computer keyboard with its keypad — the LETTER rows on the left and
 *     the NUMBER PAD on the right, KEYS ONLY: the search field at the top of
 *     the list is the readout there (PROD-1428 round 6), so none is drawn
 *     here. The list scrolls above it.
 *
 * Every key is at least 56px tall. The page writes each press into the ONE
 * search input's query and keeps the caret there, so a scanner or hardware
 * keyboard still works.
 *
 * Keys NEVER take the focus: the search input keeps its caret while a key is
 * pressed (pointer and mouse down prevented, `tabIndex -1`), and the page
 * writes the press into the query and refocuses the input inside the same
 * tap. Plain styled <button>s, not `@ordo/ui` Button: a key is no existing
 * primitive, and the app's global pill radius on `data-slot="button"` would
 * fight the pad's corners.
 *
 * `VirtualKeyButton` is that key on its own — the count boxes' numeric keypad
 * (`components/cafeteria/count-input.tsx`) is built from the same button, so
 * every on-screen key in cafeteria mode looks and behaves alike.
 */

import { cn } from '@ordo/ui/cn'
import { Check, Delete, Search, Space, X } from 'lucide-react'
import type { MouseEvent, PointerEvent } from 'react'

import {
  VIRTUAL_LETTER_ROWS,
  VIRTUAL_NUMBER_ROWS,
  type VirtualKey,
  type VirtualKeyboardLayout,
  type VirtualKeyboardRow,
  virtualKeyId,
  virtualKeyLabel,
} from '@/lib/cafeteria/virtual-keyboard'

export interface ServeKeypadProps {
  /** What has been typed so far — the readout shows it. */
  query: string
  /** One press. The page applies it to the query and refocuses the search. */
  onKey: (key: VirtualKey) => void
  /** Down the right column (default), or across the bottom with the letters left and the pad right. */
  layout?: VirtualKeyboardLayout
  className?: string
}

const KEY_BASE =
  'flex touch-manipulation select-none items-center justify-center border border-border bg-background text-2xl font-semibold tracking-tight text-foreground transition-colors focus:outline-none active:bg-muted'

/** A pressed key must not move the focus (and the caret) off the input it types into. */
function keepFocusOnInput(event: PointerEvent<HTMLElement> | MouseEvent<HTMLElement>): void {
  event.preventDefault()
}

function keyClassName(row: VirtualKeyboardRow, layout: VirtualKeyboardLayout): string {
  switch (row.variant) {
    case 'numbers':
      return cn('min-w-0 flex-1 rounded-2xl text-3xl', layout === 'bottom' ? 'h-16' : 'h-[72px]')
    case 'letters':
      // Across the bottom every letter key shares its row's width (the
      // staggered rows inset themselves); in the column the two short rows
      // keep fixed 44px keys, centered.
      return cn(
        'rounded-xl text-xl font-medium',
        layout === 'bottom' ? 'h-14 min-w-0 flex-1' : cn('h-16', row.fill ? 'min-w-0 flex-1' : 'w-11 min-w-0 shrink'),
      )
    case 'space':
      return layout === 'bottom' ? 'h-14 w-1/2 max-w-full rounded-xl' : 'h-14 w-[240px] max-w-full rounded-xl'
  }
}

/** The bottom layout staggers the letter rows like a keyboard: the home row half a key in, the z–m row a key in. */
function rowClassName(row: VirtualKeyboardRow, layout: VirtualKeyboardLayout): string | undefined {
  if (layout !== 'bottom') {
    return !row.fill ? 'justify-center' : undefined
  }
  switch (row.id) {
    case 'letters-home':
      return 'px-[3%]'
    case 'letters-bottom':
      return 'pl-[6%]'
    case 'space':
      return 'justify-center'
    default:
      return undefined
  }
}

export interface VirtualKeyButtonProps<K extends VirtualKey> {
  virtualKey: K
  /** One press, reported as the key itself; the focus stays where it was. */
  onKey: (key: K) => void
  /** Shape only (corners, size); the face, colours and type come from the key itself. */
  className?: string
}

/** One on-screen key: a plain button that never takes the focus and reports its press. */
export function VirtualKeyButton<K extends VirtualKey>({ virtualKey, onKey, className }: VirtualKeyButtonProps<K>) {
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-label={virtualKeyLabel(virtualKey)}
      data-key={virtualKeyId(virtualKey)}
      className={cn(KEY_BASE, className)}
      onPointerDown={keepFocusOnInput}
      onMouseDown={keepFocusOnInput}
      onClick={() => onKey(virtualKey)}>
      <KeyFace virtualKey={virtualKey} />
    </button>
  )
}

function KeyFace({ virtualKey }: { virtualKey: VirtualKey }) {
  switch (virtualKey.kind) {
    case 'char':
      return <>{virtualKey.char}</>
    case 'clear':
      return <X className="size-6" aria-hidden />
    case 'backspace':
      return <Delete className="size-7" aria-hidden />
    case 'space':
      return <Space className="size-6" aria-hidden />
    case 'confirm':
      return <Check className="size-8 text-emerald-600" strokeWidth={3} aria-hidden />
  }
}

function KeyRows({
  rows,
  layout,
  onKey,
}: {
  rows: readonly VirtualKeyboardRow[]
  layout: VirtualKeyboardLayout
  onKey: (key: VirtualKey) => void
}) {
  return (
    <>
      {rows.map((row) => (
        <div key={row.id} data-row={row.id} className={cn('flex gap-2', rowClassName(row, layout))}>
          {row.keys.map((virtualKey, index) =>
            virtualKey === null ? (
              <span key={`${row.id}-blank-${index}`} aria-hidden className="min-w-0 flex-1" />
            ) : (
              <VirtualKeyButton
                key={`${row.id}-${virtualKeyId(virtualKey)}`}
                virtualKey={virtualKey}
                className={keyClassName(row, layout)}
                onKey={onKey}
              />
            ),
          )}
        </div>
      ))}
    </>
  )
}

/**
 * The readout: what has been typed, large, with an ✕ that clears it. The
 * top search bar is hidden in tablet mode (PROD-1428 item 12), so this is
 * where the worker reads back the ID they are keying.
 */
function Readout({ query, onClear }: { query: string; onClear: () => void }) {
  const empty = query.length === 0
  return (
    <div
      data-testid="serve-keypad-readout"
      className="flex h-[72px] shrink-0 items-center gap-3 rounded-2xl border-2 border-primary bg-background pl-5 pr-2 ring-4 ring-primary/10">
      <Search className="size-6 shrink-0 text-muted-foreground" aria-hidden />
      <span
        aria-live="polite"
        className={cn(
          'min-w-0 flex-1 truncate whitespace-pre text-2xl font-semibold',
          empty ? 'text-muted-foreground/70' : 'text-foreground',
        )}>
        {empty ? 'Name or student ID' : query}
      </span>
      {empty ? null : (
        <button
          type="button"
          tabIndex={-1}
          aria-label="Clear search"
          data-key="clear"
          className="flex size-12 shrink-0 items-center justify-center rounded-full border border-border bg-background text-foreground"
          onPointerDown={keepFocusOnInput}
          onMouseDown={keepFocusOnInput}
          onClick={onClear}>
          <X className="size-5" aria-hidden />
        </button>
      )}
    </div>
  )
}

export function ServeKeypad({ query, onKey, layout = 'column', className }: ServeKeypadProps) {
  const numbers = (
    <div
      data-testid="serve-keypad-numbers"
      className={cn('flex flex-col gap-2', layout === 'bottom' && 'w-[300px] shrink-0')}>
      <KeyRows rows={VIRTUAL_NUMBER_ROWS} layout={layout} onKey={onKey} />
    </div>
  )
  const letters = (
    <div
      data-testid="serve-keypad-letters"
      className={cn('flex flex-col gap-2', layout === 'bottom' && 'min-w-0 flex-1')}>
      <KeyRows rows={VIRTUAL_LETTER_ROWS} layout={layout} onKey={onKey} />
    </div>
  )
  return (
    <div
      className={cn('flex flex-col gap-3', layout === 'column' && 'bg-muted p-4', className)}
      data-testid="serve-keypad"
      data-layout={layout}>
      {layout === 'column' ? <Readout query={query} onClear={() => onKey({ kind: 'clear' })} /> : null}
      <div
        role="group"
        aria-label="On-screen keyboard"
        data-testid="virtual-keyboard"
        className={cn('min-h-0', layout === 'bottom' ? 'flex gap-4' : 'flex flex-col gap-2 overflow-y-auto')}>
        {layout === 'bottom' ? (
          <>
            {letters}
            {numbers}
          </>
        ) : (
          <>
            {numbers}
            <div className="h-1" aria-hidden />
            {letters}
          </>
        )}
      </div>
    </div>
  )
}
